export interface AuthSessionEpoch {
  userId: string;
  epoch: number;
}

let currentUserId: string | null = null;
let currentEpoch = 0;
const activeControllers = new Map<number, Set<AbortController>>();

function rotateSession(nextUserId: string | null): number {
  currentUserId = nextUserId;
  currentEpoch += 1;
  for (const controllers of activeControllers.values()) {
    for (const controller of controllers) controller.abort();
  }
  activeControllers.clear();
  return currentEpoch;
}

/** Rotate the browser auth epoch and abort work owned by the prior session. */
export function setAuthSessionUser(userId: string | null): void {
  if (currentUserId === userId) return;
  rotateSession(userId);
}

/** Invalidate even an already-null session after a fresh 401 signal. */
export function invalidateAuthSession(): void {
  rotateSession(null);
}

export function authSessionEpoch(): number {
  return currentEpoch;
}

export function captureAuthSession(userId: string): AuthSessionEpoch | null {
  return currentUserId === userId ? { userId, epoch: currentEpoch } : null;
}

export function isAuthSessionCurrent(owner: AuthSessionEpoch): boolean {
  return currentUserId === owner.userId && currentEpoch === owner.epoch;
}

export function registerSessionAbortController(
  owner: AuthSessionEpoch,
  controller: AbortController,
): () => void {
  if (!isAuthSessionCurrent(owner)) {
    controller.abort();
    return () => {};
  }
  const controllers = activeControllers.get(owner.epoch) ?? new Set<AbortController>();
  controllers.add(controller);
  activeControllers.set(owner.epoch, controllers);
  return () => {
    controllers.delete(controller);
    if (controllers.size === 0) activeControllers.delete(owner.epoch);
  };
}
